import { ChangeEvent, KeyboardEvent, MouseEvent, ReactNode, useEffect, useRef, useState } from "react";
import { ArrowDownIcon, Button, Dialog, DownloadIcon, EditIcon, Heading, IconButton, LinkIcon, LockIcon, Menu, MoreIcon, Pane, Paragraph, PeopleIcon, PlusIcon, Popover, Spinner, Text, Textarea, TextInput, TrashIcon, WarningSignIcon } from "evergreen-ui";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { RampartAuthorized } from "../rampart/RampartAuthorized";
import { FMS_CHAT_GROUP } from "../rampart/RampartUtils";
import { EmptyState } from "../common/EmptyState";
import { useFmsChatStateManager } from "./FmsChatStateManager";
import { fmsChatDisplayConverter } from "./FmsChatDisplayConverter";
import { extractRowCountNote, formatSql, splitBeforeSqlBlock, stripSqlHeading } from "./fmsSqlFormat";
import "./styles/FmsChat.css";

const INVITE_ONLY_SUBTITLE = "The chat beta is invite-only. Ask in #eng if you'd like access.";

const InviteOnly = () => (
    <EmptyState title="Not available" subtitle={INVITE_ONLY_SUBTITLE}>
        <LockIcon size={60} />
    </EmptyState>
);

// Markdown tables render with a collapse toggle (expanded by default) so a
// large result table can be folded away. Each table owns its open state.
const CollapsibleTable = ({ children }: { children?: ReactNode }) => {
    const [open, setOpen] = useState(true);
    return (
        <div className="fms-table-block">
            <Text
                className="fms-table-toggle"
                size={300}
                color="muted"
                display="block"
                cursor="pointer"
                onClick={() => setOpen(v => !v)}
            >
                {open ? "▾ Table" : "▸ Table"}
            </Text>
            {open && <table>{children}</table>}
        </div>
    );
};

// The agent appends the generated query as a ```sql block; show it collapsed by
// default (like the reasoning stream) and pretty-printed when expanded.
const SqlBlock = ({ value }: { value: string }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="fms-sql-block">
            <Text
                className="fms-sql-toggle"
                size={300}
                color="muted"
                display="block"
                cursor="pointer"
                onClick={() => setOpen(v => !v)}
            >
                {open ? "▾ SQL" : "▸ SQL"}
            </Text>
            {open && <pre><code>{formatSql(value)}</code></pre>}
        </div>
    );
};

// react-markdown wraps block code in <pre><code>. Pass <pre> through and let the
// <code> renderer own the wrapper, so the SQL block is not boxed twice.
const MarkdownPre = ({ children }: { children?: ReactNode }) => <>{children}</>;

const MarkdownCode = ({ inline, className, children }: { inline?: boolean; className?: string; children?: ReactNode }) => {
    if (!inline && /language-sql/.test(className ?? "")) {
        return <SqlBlock value={String(children ?? "").replace(/\n$/, "")} />;
    }
    if (inline) {
        return <code className={className}>{children}</code>;
    }
    return <pre><code className={className}>{children}</code></pre>;
};

// Stable reference so react-markdown does not remount the renderers each render.
const MARKDOWN_COMPONENTS = { table: CollapsibleTable, pre: MarkdownPre, code: MarkdownCode };

const FmsChat = () => {
    const stepLabel = (s: string) => fmsChatDisplayConverter.thinkingStepLabel(s);

    const {
        ownedThreadRows,
        sharedThreadRows,
        activeThreadId,
        messages,
        conversationTitle,
        statusLabel,
        isBusy,
        draft,
        error,
        blocked,
        pendingDeleteTitle,
        liveThinking,
        thinkingByMsgIdx,
        csvDownloadByMsgIdx,
        onDownloadCsv,
        expandedMsgIdx,
        onToggleThoughts,
        onDraftChange,
        onSubmit,
        onOpenThread,
        onStartNew,
        onRequestDelete,
        onConfirmDelete,
        onCancelDelete,
        renamingThreadId,
        renameDraft,
        onStartRename,
        onRenameDraftChange,
        onCommitRename,
        onCancelRename,
        isViewingSharedThread,
        shareDialog,
        onOpenShare,
        onCloseShare,
        onShareEmailChange,
        onAddByEmail,
        onRemoveMember,
        deepLinkNoAccess,
        deepLinkLoadFailed,
        onCopyShareLink
    } = useFmsChatStateManager();

    // Presentational scroll machinery (VCM allows refs/effects in the view).
    // scrollRef is the transcript viewport; bottomRef is its end sentinel.
    const scrollRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const [atBottom, setAtBottom] = useState(true);
    // Reveal the scrollbar only while scrolling, hidden again shortly after.
    const [scrolling, setScrolling] = useState(false);
    const scrollIdleRef = useRef<ReturnType<typeof setTimeout>>();

    const scrollToBottom = () => bottomRef.current?.scrollIntoView({ behavior: "smooth" });

    // Track whether the viewport is parked at (near) the bottom, and show the
    // scrollbar while scrolling (auto-hidden ~0.9s after scrolling stops).
    const onTranscriptScroll = () => {
        const el = scrollRef.current;
        if (el) {
            setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
        }
        setScrolling(true);
        if (scrollIdleRef.current) {
            clearTimeout(scrollIdleRef.current);
        }
        scrollIdleRef.current = setTimeout(() => setScrolling(false), 900);
    };

    // Auto-scroll as messages, the status line, and streamed reasoning arrive,
    // but only when already at the bottom so reading earlier content is not
    // interrupted. The jump button (below) covers the scrolled-up case.
    useEffect(() => {
        if (atBottom) {
            bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, statusLabel, liveThinking, atBottom]);

    // A freshly opened thread or new conversation starts pinned to the bottom.
    useEffect(() => {
        setAtBottom(true);
    }, [activeThreadId]);

    // Auto-grow the question box: it starts at one row and expands to fit the
    // text, up to the maxHeight cap (then it scrolls). Reset to "auto" first so
    // it also shrinks back when text is deleted. Driven by draft changes, not a
    // ResizeObserver, so it cannot loop. Presentational, like the scroll effect.
    const inputRef = useRef<HTMLTextAreaElement>(null);
    useEffect(() => {
        const el = inputRef.current;
        if (el) {
            el.style.height = "auto";
            el.style.height = `${el.scrollHeight}px`;
        }
    }, [draft]);

    const onInputKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        // Enter sends; Shift+Enter inserts a newline. Skip Enter mid-IME
        // composition so a half-composed character is not submitted.
        if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            onSubmit();
        }
    };

    // Server-side mint rejection (403/401): the client is in the group
    // (RampartAuthorized gated this) but the server refused, so show the
    // same invite-only state instead of a transient error line.
    if (blocked) {
        return <InviteOnly />;
    }

    return (
        <Pane display="flex" height="100%">
            {/* minWidth 0 overrides the flex-item default (min-width: auto),
                which would otherwise let a wide answer stretch this column
                past the viewport and push the threads pane off-screen. */}
            <Pane flex={1} minWidth={0} display="flex" flexDirection="column" paddingTop={24} paddingLeft={24} paddingRight={12} paddingBottom={12}>
                {deepLinkNoAccess ? (
                    <EmptyState
                        title="You don't have access to this conversation"
                        subtitle="Ask the owner to share it with you."
                    >
                        <LockIcon size={40} color="muted" aria-hidden />
                    </EmptyState>
                ) : deepLinkLoadFailed ? (
                    <Pane height="100%" display="flex" flexDirection="column" justifyContent="center" alignItems="center" textAlign="center">
                        <WarningSignIcon size={40} color="muted" aria-hidden />
                        <Heading size={500} marginTop={12} marginBottom={8}>We couldn&apos;t load this conversation</Heading>
                        <Paragraph color="muted" marginBottom={20} maxWidth={320}>
                            It may no longer exist, or something went wrong. Start a new conversation to continue.
                        </Paragraph>
                        <Button appearance="primary" iconBefore={PlusIcon} onClick={onStartNew}>Start a new conversation</Button>
                    </Pane>
                ) : (
                    <>
                        <Heading size={600} marginBottom={16}>{conversationTitle}</Heading>
                        <Pane flex={1} minHeight={0} position="relative" display="flex" flexDirection="column">
                            <Pane ref={scrollRef} className={scrolling ? "fms-transcript fms-scrolling" : "fms-transcript"} flex={1} minHeight={0} overflowY="auto" onScroll={onTranscriptScroll}>
                            {messages.length === 0 && !isBusy && (
                                <Paragraph color="muted">
                                    Ask about sales, profit, payouts, products, or launches.
                                    Answers come from the live finance database.
                                </Paragraph>
                            )}
                            {messages.map((m, i) => {
                                // Place Download CSV directly under the result table (above the
                                // collapsible SQL block): split the answer at the SQL block and
                                // strip the redundant "**SQL**" label from the SQL portion. Lift the
                                // "(showing N of M rows)" footer onto the download row only when there
                                // is a download to host it; otherwise leave the footer in the markdown
                                // so answers without a result_table snapshot (e.g. ones persisted
                                // before this feature) still render it in place.
                                const { beforeSql, sqlBlock } = splitBeforeSqlBlock(m.content);
                                const tableMarkdown = csvDownloadByMsgIdx[i] ? extractRowCountNote(beforeSql) : { content: beforeSql, note: null };
                                const sqlMarkdown = stripSqlHeading(sqlBlock);
                                const rowNote = csvDownloadByMsgIdx[i]?.rowNote ?? tableMarkdown.note;
                                return (
                                    <Pane key={i}>
                                        {m.role === "ai" && thinkingByMsgIdx[i] && (
                                            <Pane marginBottom={4}>
                                                <Text
                                                    className="fms-thoughts-toggle"
                                                    size={300}
                                                    color="muted"
                                                    cursor="pointer"
                                                    onClick={() => onToggleThoughts(i)}
                                                >
                                                    {expandedMsgIdx[i] ? "▾ Thoughts" : "▸ Thoughts"}
                                                </Text>
                                                {expandedMsgIdx[i] && (
                                                    <Pane className="fms-thinking" marginTop={4}>
                                                        {thinkingByMsgIdx[i].map((seg, j) => (
                                                            <Paragraph key={j} className="fms-thinking-seg">
                                                                <strong>{stepLabel(seg.step)}: </strong>{seg.text}
                                                            </Paragraph>
                                                        ))}
                                                    </Pane>
                                                )}
                                            </Pane>
                                        )}
                                        {m.role === "ai" ? (
                                            <>
                                                <Pane marginBottom={csvDownloadByMsgIdx[i] ? 4 : 12} className="fms-markdown">
                                                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>{tableMarkdown.content}</ReactMarkdown>
                                                </Pane>
                                                {csvDownloadByMsgIdx[i] && (
                                                    <Pane marginBottom={12} display="flex" alignItems="center" justifyContent={rowNote ? "space-between" : "flex-end"} gap={8}>
                                                        {rowNote && <Text size={300} color="muted" fontStyle="italic">({rowNote})</Text>}
                                                        <Text
                                                            is="button"
                                                            type="button"
                                                            className="fms-csv-download"
                                                            size={300}
                                                            color="muted"
                                                            cursor="pointer"
                                                            display="inline-flex"
                                                            alignItems="center"
                                                            onClick={() => onDownloadCsv(i)}
                                                        >
                                                            <DownloadIcon size={12} marginRight={4} />
                                                            Download CSV
                                                        </Text>
                                                    </Pane>
                                                )}
                                                {sqlMarkdown && (
                                                    <Pane marginBottom={12} className="fms-markdown">
                                                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>{sqlMarkdown}</ReactMarkdown>
                                                    </Pane>
                                                )}
                                            </>
                                        ) : (
                                            // The user's turn is a right-aligned bubble that hugs
                                            // its content up to 80% of the column. Human input is
                                            // untrusted free text and stays plain; pre-wrap keeps
                                            // Shift+Enter line breaks.
                                            <Pane marginBottom={12} display="flex" justifyContent="flex-end">
                                                <Pane padding={12} borderRadius={8} background="blueTint" maxWidth="80%">
                                                    <Paragraph whiteSpace="pre-wrap" wordBreak="break-word">{m.content}</Paragraph>
                                                </Pane>
                                            </Pane>
                                        )}
                                    </Pane>
                                );
                            })}
                            {liveThinking !== null && liveThinking.length > 0 && (
                                <Pane className="fms-thinking" marginBottom={12}>
                                    {liveThinking.map((seg, j) => (
                                        <Paragraph key={j} className="fms-thinking-seg" color="muted">
                                            <strong>{stepLabel(seg.step)}: </strong>{seg.text}
                                        </Paragraph>
                                    ))}
                                </Pane>
                            )}
                            {statusLabel !== null && (
                                <Pane display="flex" alignItems="center" gap={8} marginBottom={12}>
                                    <Spinner size={16} />
                                    <Text color="muted">{statusLabel}</Text>
                                </Pane>
                            )}
                            {error !== null && (
                                <Paragraph color="danger" marginBottom={12}>{error}</Paragraph>
                            )}
                            <div ref={bottomRef} />
                            </Pane>
                            {!atBottom && (
                                <IconButton
                                    className="fms-jump-to-bottom"
                                    icon={ArrowDownIcon}
                                    aria-label="Scroll to latest"
                                    title="Scroll to latest"
                                    borderRadius={16}
                                    position="absolute"
                                    bottom={8}
                                    left="50%"
                                    onClick={scrollToBottom}
                                />
                            )}
                        </Pane>
                        {/* alignItems flex-end keeps Send pinned to the bottom as the
                            textarea grows. It starts at one row (minHeight overrides
                            evergreen's default 80) and auto-grows up to maxHeight (see
                            the effect above). Enter sends; Shift+Enter adds a newline.
                            A shared-with-me thread is read-only: both controls are
                            disabled and a small hint sits above the composer. */}
                        {isViewingSharedThread && (
                            <Pane marginTop={16} marginBottom={-8}>
                                <Text size={300} color="muted" fontStyle="italic">View-only — shared with you.</Text>
                            </Pane>
                        )}
                        <Pane display="flex" alignItems="flex-end" gap={8} marginTop={16}>
                            <Textarea
                                ref={inputRef}
                                className="fms-input"
                                flex={1}
                                rows={1}
                                minHeight={38}
                                maxHeight={160}
                                placeholder="Write a question..."
                                value={draft}
                                disabled={isBusy || isViewingSharedThread}
                                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onDraftChange(e.target.value)}
                                onKeyDown={onInputKeyDown}
                            />
                            <Button appearance="primary" disabled={isBusy || isViewingSharedThread} onClick={onSubmit}>
                                Send
                            </Button>
                        </Pane>
                    </>
                )}
            </Pane>

            <Pane width={260} flexShrink={0} borderLeft="muted" position="relative" display="flex" flexDirection="column">
                {/* Extra bottom padding keeps the last thread scrollable above
                    the floating new-conversation button. */}
                <Pane flex={1} overflowY="auto" padding={16} paddingBottom={80}>
                    {ownedThreadRows.map(row => (
                        <Pane
                            key={row.id}
                            className="fms-thread-row"
                            display="flex"
                            alignItems="center"
                            gap={4}
                            padding={8}
                            marginBottom={8}
                            borderRadius={4}
                            background={row.id === activeThreadId ? "tint2" : undefined}
                            cursor="pointer"
                            onClick={() => onOpenThread(row.id)}
                        >
                            <Pane flex={1} minWidth={0}>
                                {renamingThreadId === row.id ? (
                                    // Inline rename: Enter or blur commits, Esc
                                    // cancels. stopPropagation so editing does not
                                    // open the thread underneath.
                                    <TextInput
                                        autoFocus
                                        aria-label="Rename conversation"
                                        width="100%"
                                        height={24}
                                        value={renameDraft}
                                        onClick={(e: MouseEvent<HTMLInputElement>) => e.stopPropagation()}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => onRenameDraftChange(e.target.value)}
                                        onBlur={onCommitRename}
                                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                                            if (e.key === "Enter") {
                                                onCommitRename();
                                            } else if (e.key === "Escape") {
                                                onCancelRename();
                                            }
                                        }}
                                    />
                                ) : (
                                    <Text
                                        size={300}
                                        display="block"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        whiteSpace="nowrap"
                                        fontWeight={row.id === activeThreadId ? 600 : 400}
                                    >
                                        {row.title}
                                    </Text>
                                )}
                                <Text size={300} color="muted">{row.timestamp}</Text>
                            </Pane>
                            {/* Overflow menu (Rename / Share / Delete). The button
                                is revealed by .fms-thread-row:hover and focus-within
                                (FmsChat.css); focus-within keeps it visible while
                                the Popover is open. The wrapping Pane stops a
                                click from also opening the thread underneath. */}
                            <Pane
                                className="fms-thread-actions"
                                onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
                            >
                                <Popover
                                    position="bottom-right"
                                    content={({ close }: { close: () => void }) => (
                                        <Menu>
                                            <Menu.Group>
                                                <Menu.Item icon={EditIcon} onSelect={() => { close(); onStartRename(row.id); }}>
                                                    Rename
                                                </Menu.Item>
                                                <Menu.Item icon={PeopleIcon} onSelect={() => { close(); onOpenShare(row.id); }}>
                                                    Share
                                                </Menu.Item>
                                                <Menu.Item icon={TrashIcon} intent="danger" onSelect={() => { close(); onRequestDelete(row.id); }}>
                                                    Delete
                                                </Menu.Item>
                                            </Menu.Group>
                                        </Menu>
                                    )}
                                >
                                    <IconButton
                                        icon={MoreIcon}
                                        size="small"
                                        appearance="minimal"
                                        aria-label={`Conversation options for ${row.title}`}
                                    />
                                </Popover>
                            </Pane>
                        </Pane>
                    ))}

                    {/* Conversations shared with the current user (read-only). */}
                    {sharedThreadRows.length > 0 && (
                        <Pane marginTop={16}>
                            <Heading size={100} color="muted" marginBottom={8} paddingX={8}>
                                Shared with me
                            </Heading>
                            {sharedThreadRows.map(row => (
                                <Pane
                                    key={row.id}
                                    className="fms-thread-row"
                                    display="flex"
                                    alignItems="center"
                                    gap={4}
                                    padding={8}
                                    marginBottom={8}
                                    borderRadius={4}
                                    background={row.id === activeThreadId ? "tint2" : undefined}
                                    cursor="pointer"
                                    onClick={() => onOpenThread(row.id)}
                                >
                                    <Pane flex={1} minWidth={0}>
                                        <Text
                                            size={300}
                                            display="block"
                                            overflow="hidden"
                                            textOverflow="ellipsis"
                                            whiteSpace="nowrap"
                                            fontWeight={row.id === activeThreadId ? 600 : 400}
                                        >
                                            {row.title}
                                        </Text>
                                        {row.sharedByLabel && (
                                            <Text size={300} color="muted">{row.sharedByLabel}</Text>
                                        )}
                                    </Pane>
                                </Pane>
                            ))}
                        </Pane>
                    )}
                </Pane>
                <IconButton
                    icon={<PlusIcon color="white" size={20} />}
                    appearance="primary"
                    aria-label="New conversation"
                    title="New conversation"
                    position="absolute"
                    bottom={16}
                    right={16}
                    width={48}
                    height={48}
                    borderRadius={24}
                    boxShadow="0 2px 8px rgba(16, 24, 64, 0.3)"
                    onClick={onStartNew}
                />
            </Pane>

            <Dialog
                isShown={pendingDeleteTitle !== null}
                title="Delete conversation"
                intent="danger"
                confirmLabel="Delete"
                onConfirm={(close) => {
                    onConfirmDelete();
                    close();
                }}
                onCloseComplete={onCancelDelete}
            >
                {`Delete "${pendingDeleteTitle ?? "Untitled"}"? This cannot be undone.`}
            </Dialog>

            {/* Share dialog (owned threads). Adds and removals both apply
                immediately; "Done" just closes. */}
            {shareDialog !== null && (
                <Dialog
                    isShown
                    title={`Share "${shareDialog.threadTitle}"`}
                    confirmLabel="Done"
                    hasCancel={false}
                    onConfirm={onCloseShare}
                    onCloseComplete={onCloseShare}
                >
                    <Paragraph size={300} color="muted" marginBottom={16}>
                        People you add can view this conversation, including any new questions you ask in it. Only teammates with FMS chat access can be added.
                    </Paragraph>
                    <Pane display="flex" alignItems="center" marginBottom={16}>
                        <TextInput flex={1} marginRight={8} readOnly value={shareDialog.linkUrl} aria-label="Conversation link" />
                        <Button iconBefore={LinkIcon} onClick={onCopyShareLink}>
                            {shareDialog.linkCopied ? "Copied" : "Copy link"}
                        </Button>
                    </Pane>
                    {/* Visually hidden live region so screen readers announce the
                        copy result (the button's label swap alone is not announced). */}
                    <Pane role="status" aria-live="polite" position="absolute" width={1} height={1} overflow="hidden" whiteSpace="nowrap">
                        {shareDialog.linkCopied ? "Conversation link copied to clipboard" : ""}
                    </Pane>

                    <Heading size={100} marginBottom={8}>Shared with</Heading>
                    {shareDialog.currentMembers.length === 0 ? (
                        <Paragraph size={300} color="muted" marginBottom={16}>Not shared with anyone yet.</Paragraph>
                    ) : (
                        <Pane marginBottom={16}>
                            {shareDialog.currentMembers.map(m => (
                                <Pane key={m.stytchUserId} display="flex" alignItems="center" justifyContent="space-between" marginBottom={4}>
                                    <Text size={400}>{m.name}</Text>
                                    <IconButton
                                        icon={TrashIcon}
                                        size="small"
                                        appearance="minimal"
                                        intent="danger"
                                        aria-label={`Remove ${m.name}`}
                                        onClick={() => onRemoveMember(m.stytchUserId)}
                                    />
                                </Pane>
                            ))}
                        </Pane>
                    )}

                    <Heading size={100} marginBottom={8}>Add someone</Heading>
                    <Pane display="flex">
                        <TextInput
                            flex={1}
                            marginRight={8}
                            type="email"
                            placeholder="name@company.com"
                            value={shareDialog.emailInput}
                            disabled={shareDialog.isAdding}
                            isInvalid={shareDialog.addError !== null}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => onShareEmailChange(e.target.value)}
                            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                                if (e.key === "Enter") {
                                    onAddByEmail();
                                }
                            }}
                        />
                        <Button
                            appearance="primary"
                            isLoading={shareDialog.isAdding}
                            disabled={shareDialog.emailInput.trim() === ""}
                            onClick={onAddByEmail}
                        >
                            Add
                        </Button>
                    </Pane>
                    {shareDialog.addError !== null && (
                        <Paragraph size={300} color="danger" marginTop={8}>{shareDialog.addError}</Paragraph>
                    )}
                </Dialog>
            )}
        </Pane>
    );
};

// Group gate via the standard wrapper, like every other gated page. It
// denies by default until the user resolves (no authorized-view flash on
// load), and the chat UI + its token-minting effects only mount once the
// user is in the group. Server-side /langgraph/token 403 stays the real
// access control; the inner component handles that via `blocked`.
export const FmsChatPage = () => (
    <RampartAuthorized
        requiredGroups={[FMS_CHAT_GROUP]}
        unauthorizedView={<InviteOnly />}
    >
        <FmsChat />
    </RampartAuthorized>
);
